import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { parseJson } from './memoryUtils';
import { hasProfileSession, profileHeaders } from './profileAuth';

import './Comments.css';

const Comments = ({ memoryId, profileId, profileSettings = {} }) => {
  const [comments, setComments] = useState([]);
  const [author, setAuthor] = useState('');
  const [content, setContent] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const adminToken = hasProfileSession(profileId);
  const adminHeaders = profileHeaders(profileId);

  const loadComments = async () => {
    const response = await axios.get(`/api/comments/${memoryId}`, { headers: adminHeaders });
    setComments(response.data || []);
  };

  useEffect(() => {
    loadComments().catch(() => setMessage('Komentáře se nepodařilo načíst.'));
  }, [memoryId, profileId]);

  const addComment = async () => {
    setMessage('');
    if (!content.trim()) {
      setMessage('Komentář nemůže být prázdný.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post('/api/comments', {
        memory_id: memoryId,
        author: author || 'Anonym',
        content,
        metadata: '{}',
      });
      setContent('');
      setMessage(response.data.status === 'pending' ? 'Komentář byl odeslán ke schválení.' : 'Komentář byl přidán.');
      await loadComments();
    } catch {
      setMessage('Komentář se nepodařilo přidat.');
    } finally {
      setSubmitting(false);
    }
  };

  const saveComment = async (comment) => {
    const draft = editing[comment.id];
    if (!draft) return;

    try {
      await axios.put(
        `/api/comments/${comment.id}`,
        {
          author: draft.author,
          content: draft.content,
          metadata: JSON.stringify(draft.metadata),
        },
        { headers: adminHeaders },
      );
      setEditing((prev) => {
        const updated = { ...prev };
        delete updated[comment.id];
        return updated;
      });
      loadComments();
    } catch {
      setMessage('Komentář se nepodařilo uložit.');
    }
  };

  const setCommentStatus = async (comment, status) => {
    const metadata = { ...parseJson(comment.metadata, {}), status };
    try {
      await axios.put(`/api/comments/${comment.id}`, { author: comment.author, content: comment.content, metadata: JSON.stringify(metadata) }, { headers: adminHeaders });
      await loadComments();
    } catch {
      setMessage('Komentář se nepodařilo uložit.');
    }
  };

  const startEditing = (comment) => {
    setEditing((prev) => ({
      ...prev,
      [comment.id]: {
        author: comment.author || '',
        content: comment.content || '',
        metadata: parseJson(comment.metadata, {}),
      },
    }));
  };

  return (
    <section className="comments">
      <h2>Komentáře</h2>
      {message && <p className="comments-message">{message}</p>}

      <div className="comment-form">
        <input aria-label="Vaše jméno" value={author} maxLength={255} onChange={(event) => setAuthor(event.target.value)} placeholder={profileSettings.allowAnonymous === false ? 'Vaše jméno' : 'Jméno (nepovinné)'} />
        <textarea aria-label="Komentář" maxLength={5000} value={content} onChange={(event) => setContent(event.target.value)} placeholder="Napište komentář" />
        <button type="button" onClick={addComment} disabled={submitting}>
          Přidat komentář
        </button>
      </div>

      <div className="comment-list">
        {comments.length === 0 && <p>Zatím zde nejsou žádné komentáře.</p>}
        {comments.map((comment) => {
          const draft = editing[comment.id];
          const metadata = parseJson(comment.metadata, {});

          return (
            <article key={comment.id} className={`comment-item comment-${metadata.status || 'approved'}`}>
              {draft ? (
                <>
                  <input
                    value={draft.author}
                    onChange={(event) =>
                      setEditing((prev) => ({
                        ...prev,
                        [comment.id]: { ...draft, author: event.target.value },
                      }))
                    }
                  />
                  <textarea
                    value={draft.content}
                    onChange={(event) =>
                      setEditing((prev) => ({
                        ...prev,
                        [comment.id]: { ...draft, content: event.target.value },
                      }))
                    }
                  />
                  <div className="comment-actions">
                    <button type="button" onClick={() => saveComment(comment)}>
                      Uložit
                    </button>
                    <button type="button" onClick={() => setEditing((prev) => ({ ...prev, [comment.id]: null }))}>
                      Zrušit
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <header>
                    <strong>{comment.author || 'Anonym'}</strong>
                    {metadata.status === 'rejected' && <span>Skryto</span>}
                    {metadata.status === 'pending' && <span>Čeká na schválení</span>}
                  </header>
                  <p>{comment.content}</p>
                  {adminToken && (
                    <div className="comment-actions">
                      <button type="button" onClick={() => startEditing(comment)}>
                        Upravit
                      </button>
                      {metadata.status !== 'approved' && (
                        <button type="button" onClick={() => setCommentStatus(comment, 'approved')}>
                          Zveřejnit
                        </button>
                      )}
                      <button type="button" onClick={() => setCommentStatus(comment, 'rejected')}>
                        Skrýt
                      </button>
                    </div>
                  )}
                </>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default Comments;
